/**
 * Subscription lifecycle state machine.
 *
 * The subscription settings page exposes a small, fixed set of management
 * actions ("cancel", "resume renewal", "cancel scheduled change"). Whether a
 * given action is appropriate depends entirely on a few orthogonal facts about
 * the current user's subscription:
 *
 *   - is the user on a paid plan at all?
 *   - is renewal currently turned off?
 *   - is there an upcoming scheduled plan switch?
 *
 * Encoding those facts as ad-hoc `if`-clauses scattered across multiple
 * components produced two real-world bugs:
 *
 *   1. A Pro user who scheduled a downgrade to Plus had `will_renew=false`,
 *      which silently hid the inline "Cancel subscription" link — leaving them
 *      with no way to cancel everything from the page.
 *   2. The "Keep current plan instead" escape hatch only existed inside the
 *      contextual upcoming-plan notice, not as a top-level action below the
 *      plans where users were looking for it.
 *
 * This module lifts those rules out of the rendering layer into a single,
 * testable state machine. Every consumer derives the lifecycle state from the
 * data and asks for the available actions — no `if (will_renew && !upcoming &&
 * isPaid)` ladders anywhere else in the app.
 */

export const LIFECYCLE_STATES = Object.freeze({
  /** Free plan (or access-grant-only): no paid Stripe subscription to manage. */
  FREE: "free",
  /** Paid, will renew, no pending plan switch — the default healthy state. */
  ACTIVE: "active",
  /** Paid, will NOT renew, no pending plan switch — ends at period end. */
  RENEWAL_CANCELLED: "renewal_cancelled",
  /** Paid, with a scheduled plan switch (e.g. Pro -> Plus) at period end. */
  SWITCHING: "switching",
});

export const LIFECYCLE_ACTIONS = Object.freeze({
  /** Cancel the subscription (and any scheduled switch) at period end. */
  CANCEL_SUBSCRIPTION: "cancel_subscription",
  /** Turn renewal back on for a subscription that was set to cancel. */
  RESUME_RENEWAL: "resume_renewal",
  /** Drop the pending plan switch and stay on the current plan. */
  CANCEL_SCHEDULED_CHANGE: "cancel_scheduled_change",
});

/**
 * State -> ordered list of actions that should be offered in the UI.
 *
 * Order matters: the UI renders rows in this order, so non-destructive
 * actions ("Keep current plan") come before destructive ones ("Cancel
 * subscription"). When you add a new action, make a single edit here.
 */
const ACTIONS_BY_STATE = Object.freeze({
  [LIFECYCLE_STATES.FREE]: Object.freeze([]),
  [LIFECYCLE_STATES.ACTIVE]: Object.freeze([LIFECYCLE_ACTIONS.CANCEL_SUBSCRIPTION]),
  [LIFECYCLE_STATES.RENEWAL_CANCELLED]: Object.freeze([LIFECYCLE_ACTIONS.RESUME_RENEWAL]),
  [LIFECYCLE_STATES.SWITCHING]: Object.freeze([
    LIFECYCLE_ACTIONS.CANCEL_SCHEDULED_CHANGE,
    LIFECYCLE_ACTIONS.CANCEL_SUBSCRIPTION,
  ]),
});

/**
 * True iff the subscription represents a real paid Stripe subscription
 * (not free, not access-grant-only).
 */
export const isPaidSubscription = (subscription) => {
  if (!subscription) return false;
  const plan = String(subscription.plan || "free").toLowerCase();
  if (plan === "free") return false;
  return Number(subscription.price || 0) > 0;
};

/**
 * Derive the lifecycle state from the live subscription payload.
 *
 * @param {object} args
 * @param {object|null} args.subscription - the `currentSubscription` payload
 * @param {object|null} args.upcomingSubscription - the first upcoming row, if any
 * @returns {string} one of `LIFECYCLE_STATES`
 */
export const deriveLifecycleState = ({ subscription, upcomingSubscription } = {}) => {
  if (!isPaidSubscription(subscription)) return LIFECYCLE_STATES.FREE;
  if (upcomingSubscription) return LIFECYCLE_STATES.SWITCHING;
  if (subscription?.will_renew === false) return LIFECYCLE_STATES.RENEWAL_CANCELLED;
  return LIFECYCLE_STATES.ACTIVE;
};

/**
 * Returns the ordered list of actions available for `state`.
 * Always returns a (possibly empty) array — never null/undefined.
 */
export const getAvailableActions = (state) => ACTIONS_BY_STATE[state] || [];

/**
 * Convenience: returns true if `action` is allowed for the given state.
 * Useful at action-call sites that want a guard without re-deriving the list.
 */
export const isActionAvailable = (state, action) =>
  getAvailableActions(state).includes(action);
